import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCart } from '../commerce/CartContext';
import { formatCurrency } from '../data';

const { FiX, FiMinus, FiPlus, FiTrash2 } = FiIcons;

function CartDrawer() {
  const {
    items,
    subtotal,
    drawerOpen,
    setDrawerOpen,
    updateQuantity,
    removeItem
  } = useCart();
  const closeButton = useRef(null);

  useEffect(() => {
    if (drawerOpen) {
      closeButton.current?.focus();
    }
  }, [drawerOpen]);

  if (!drawerOpen) {
    return null;
  }

  return (
    <div className="drawer-layer" role="presentation">
      <button
        className="drawer-backdrop"
        onClick={() => setDrawerOpen(false)}
        aria-label="Close cart"
      />
      <aside
        className="cart-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-title"
      >
        <header className="drawer-header">
          <div>
            <span className="eyebrow">Your Selection</span>
            <h2 id="cart-title">Shopping Bag</h2>
          </div>
          <button
            ref={closeButton}
            className="icon-button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close cart"
          >
            <SafeIcon icon={FiX} />
          </button>
        </header>

        <div className="drawer-items">
          {items.length === 0 ? (
            <div className="empty-cart">
              <h3>Your bag is empty</h3>
              <p>Discover a saree made for your story.</p>
              <Link
                className="gold-button"
                to="/shop"
                onClick={() => setDrawerOpen(false)}
              >
                Shop the collection
              </Link>
            </div>
          ) : items.map((item) => (
            <article className="cart-line" key={item.key}>
              <img src={item.image} alt={item.imageAlt} />
              <div className="cart-line-copy">
                <h3>{item.name}</h3>
                {item.variantName && <p>{item.variantName}</p>}
                <strong>{formatCurrency(item.price)}</strong>
                <div className="quantity-row">
                  <button
                    onClick={() => updateQuantity(item.key, item.quantity - 1)}
                    aria-label={`Decrease ${item.name} quantity`}
                  >
                    <SafeIcon icon={FiMinus} />
                  </button>
                  <span aria-live="polite">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.key, item.quantity + 1)}
                    aria-label={`Increase ${item.name} quantity`}
                  >
                    <SafeIcon icon={FiPlus} />
                  </button>
                  <button
                    className="remove-button"
                    onClick={() => removeItem(item.key)}
                    aria-label={`Remove ${item.name}`}
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {items.length > 0 && (
          <footer className="drawer-footer">
            <div className="total-row">
              <span>Subtotal</span>
              <strong>{formatCurrency(subtotal)}</strong>
            </div>
            <p>
              Authoritative prices, stock, promotions, and delivery are
              verified securely at checkout.
            </p>
            <div className="drawer-action-grid">
              <Link
                className="outline-button"
                to="/cart"
                onClick={() => setDrawerOpen(false)}
              >
                View cart
              </Link>
              <Link
                className="gold-button"
                to="/checkout"
                onClick={() => setDrawerOpen(false)}
              >
                Checkout
              </Link>
            </div>
          </footer>
        )}
      </aside>
    </div>
  );
}

export default CartDrawer;